import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  LayoutGrid, 
  Film, 
  Plus, 
  ChevronRight,
  LogOut,
  Loader2,
  FolderOpen,
  Video,
  Layers,
  Users,
  Sparkles,
  Music,
  BarChart2,
  Download,
  TrendingUp,
  User
} from 'lucide-react';
import { api } from '../../services/api';

const MONTHS = ['Jan','Fev','Mar','Abr','Mai','Jun','Jul','Ago','Set','Out','Nov','Dez'];

const AdminDashboard = ({ onAdminLogout }) => {
  const [collections, setCollections] = useState([]);
  const [stats, setStats] = useState({ totalScenes: 0, totalCollections: 0, totalSounds: 0 });
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('dashboard');
  const [report, setReport] = useState(null);
  const [reportLoading, setReportLoading] = useState(false);
  const [reportMonth, setReportMonth] = useState(new Date().getMonth() + 1);
  const [reportYear, setReportYear] = useState(new Date().getFullYear());

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    if (activeTab === 'report') loadReport();
  }, [activeTab, reportMonth, reportYear]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [collectionsData, packsData, soundsData] = await Promise.all([
        api.getCollections(),
        api.getPacks(),
        api.listSoundsAdmin().catch(() => [])
      ]);
      setCollections(collectionsData);
      setStats({
        totalScenes: packsData.length,
        totalCollections: collectionsData.length,
        totalSounds: soundsData.length
      });
    } catch (err) {
      console.error('Erro ao carregar dados:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadReport = async () => {
    try {
      setReportLoading(true);
      const data = await api.getAdminReport(reportMonth, reportYear);
      setReport(data);
    } catch (err) {
      console.error('Erro ao carregar relatório:', err);
    } finally {
      setReportLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 rounded-2xl bg-[#FFCC00]/10 flex items-center justify-center">
            <Loader2 className="w-6 h-6 animate-spin text-[#FFCC00]" />
          </div>
          <p className="text-white/40 text-sm">Carregando...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505] relative">
      {/* Ambient Glow Background */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-[-20%] left-[10%] w-[600px] h-[600px] bg-[#FFCC00]/[0.03] rounded-full blur-[120px]" />
        <div className="absolute bottom-[-10%] right-[5%] w-[500px] h-[500px] bg-[#FFCC00]/[0.02] rounded-full blur-[100px]" />
      </div>

      {/* Glass Header */}
      <header className="sticky top-0 z-50 backdrop-blur-2xl bg-white/[0.02] border-b border-white/[0.05]">
        <div className="max-w-6xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#FFCC00]/20 to-[#FFCC00]/5 border border-[#FFCC00]/20 flex items-center justify-center shadow-lg shadow-[#FFCC00]/10">
                <LayoutGrid className="w-5 h-5 text-[#FFCC00]" />
              </div>
              <div>
                <h1 className="text-white font-medium tracking-tight">Admin</h1>
                <p className="text-white/30 text-xs">Pack Cinematográfico</p>
              </div>
            </div>
            
            <div className="flex items-center gap-3">
              <Link 
                to="/"
                className="px-4 py-2 text-white/50 hover:text-white text-sm transition-colors"
              >
                Ver Site
              </Link>
              <button
                onClick={() => setActiveTab(activeTab === 'report' ? 'dashboard' : 'report')}
                className={`flex items-center gap-2 px-4 py-2 rounded-xl border text-sm transition-all ${activeTab === 'report' ? 'bg-[#FFCC00]/15 border-[#FFCC00]/30 text-[#FFCC00]' : 'bg-white/[0.03] border-white/[0.06] text-white/70 hover:text-white'}`}
              >
                <BarChart2 className="w-4 h-4" />
                <span>Relatório</span>
              </button>
              <button 
                onClick={onAdminLogout}
                className="flex items-center gap-2 px-4 py-2 rounded-xl bg-white/[0.03] hover:bg-white/[0.06] border border-white/[0.06] text-white/70 hover:text-white text-sm transition-all"
              >
                <LogOut className="w-4 h-4" />
                <span>Sair</span>
              </button>
            </div>
          </div>
        </div>
      </header>

      <div className="relative z-10 max-w-6xl mx-auto px-6 py-8">
        {activeTab === 'report' ? (
          <div>
            {/* Filtros do relatório */}
            <div className="flex items-center gap-3 mb-6 flex-wrap">
              <select
                value={reportMonth}
                onChange={e => setReportMonth(Number(e.target.value))}
                className="bg-white/[0.05] border border-white/10 text-white rounded-xl px-4 py-2 text-sm"
              >
                {MONTHS.map((m, i) => <option key={i+1} value={i+1}>{m}</option>)}
              </select>
              <select
                value={reportYear}
                onChange={e => setReportYear(Number(e.target.value))}
                className="bg-white/[0.05] border border-white/10 text-white rounded-xl px-4 py-2 text-sm"
              >
                {[2025, 2026, 2027].map(y => <option key={y} value={y}>{y}</option>)}
              </select>
              <button onClick={loadReport} className="px-4 py-2 rounded-xl bg-[#FFCC00]/10 border border-[#FFCC00]/20 text-[#FFCC00] text-sm hover:bg-[#FFCC00]/15 transition-all">
                Atualizar
              </button>
            </div>

            {reportLoading ? (
              <div className="flex items-center justify-center py-20">
                <Loader2 className="w-6 h-6 animate-spin text-[#FFCC00]" />
              </div>
            ) : report ? (
              <div className="space-y-6">
                {/* Cards de totais */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {[
                    { label: 'Total Downloads', value: report.total_downloads, icon: <Download className="w-5 h-5 text-[#FFCC00]" />, color: '[#FFCC00]' },
                    { label: 'Mês Anterior', value: report.prev_month_downloads, icon: <TrendingUp className="w-5 h-5 text-blue-400" />, color: 'blue-400' },
                    { label: 'Cenas (site)', value: report.by_type.pack, icon: <Video className="w-5 h-5 text-purple-400" />, color: 'purple-400' },
                    { label: 'Cenas (plugin)', value: report.by_type.pack_plugin, icon: <Film className="w-5 h-5 text-pink-400" />, color: 'pink-400' },
                  ].map((c, i) => (
                    <div key={i} className="backdrop-blur-xl bg-white/[0.02] rounded-2xl border border-white/[0.05] p-5">
                      <div className="flex items-start justify-between">
                        <div>
                          <p className="text-white/40 text-xs mb-1">{c.label}</p>
                          <p className="text-3xl font-light text-white">{c.value}</p>
                        </div>
                        <div className="w-10 h-10 rounded-xl bg-white/[0.05] flex items-center justify-center">{c.icon}</div>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Top Cenas */}
                  <div className="backdrop-blur-xl bg-white/[0.02] rounded-2xl border border-white/[0.05] overflow-hidden">
                    <div className="px-5 py-4 border-b border-white/[0.05] flex items-center gap-2">
                      <Film className="w-4 h-4 text-[#FFCC00]/60" />
                      <h3 className="text-white font-medium text-sm">Top 10 Cenas</h3>
                    </div>
                    {report.top_scenes.length === 0 ? (
                      <p className="text-white/30 text-sm text-center py-8">Nenhum download registrado ainda</p>
                    ) : (
                      <div className="divide-y divide-white/[0.03]">
                        {report.top_scenes.map((s, i) => (
                          <div key={s.id} className="flex items-center justify-between px-5 py-3">
                            <div className="flex items-center gap-3">
                              <span className="text-white/20 text-xs w-4">{i+1}</span>
                              <div>
                                <p className="text-white text-sm">{s.title || s.id}</p>
                                <p className="text-white/30 text-xs">{s.type}</p>
                              </div>
                            </div>
                            <span className="text-[#FFCC00] text-sm font-medium">{s.downloads}x</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Top Usuários */}
                  <div className="backdrop-blur-xl bg-white/[0.02] rounded-2xl border border-white/[0.05] overflow-hidden">
                    <div className="px-5 py-4 border-b border-white/[0.05] flex items-center gap-2">
                      <User className="w-4 h-4 text-[#FFCC00]/60" />
                      <h3 className="text-white font-medium text-sm">Top 10 Usuários</h3>
                    </div>
                    {report.top_users.length === 0 ? (
                      <p className="text-white/30 text-sm text-center py-8">Nenhum download registrado ainda</p>
                    ) : (
                      <div className="divide-y divide-white/[0.03]">
                        {report.top_users.map((u, i) => (
                          <div key={u.email} className="flex items-center justify-between px-5 py-3">
                            <div className="flex items-center gap-3">
                              <span className="text-white/20 text-xs w-4">{i+1}</span>
                              <p className="text-white text-sm">{u.email}</p>
                            </div>
                            <span className="text-[#FFCC00] text-sm font-medium">{u.downloads}x</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                {/* SFX e Músicas */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="backdrop-blur-xl bg-white/[0.02] rounded-2xl border border-white/[0.05] p-5">
                    <p className="text-white/40 text-xs mb-1">Downloads de Músicas</p>
                    <p className="text-3xl font-light text-white">{report.by_type.sound}</p>
                  </div>
                  <div className="backdrop-blur-xl bg-white/[0.02] rounded-2xl border border-white/[0.05] p-5">
                    <p className="text-white/40 text-xs mb-1">Downloads de SFX</p>
                    <p className="text-3xl font-light text-white">{report.by_type.sfx}</p>
                  </div>
                </div>
              </div>
            ) : (
              <p className="text-white/30 text-center py-20">Nenhum dado encontrado para o período selecionado</p>
            )}
          </div>
        ) : (
        <div>
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-10">
          {/* Total Scenes */}
          <div className="group relative backdrop-blur-xl bg-white/[0.02] rounded-2xl border border-white/[0.05] p-6 hover:bg-white/[0.04] transition-all duration-500 hover:shadow-2xl hover:shadow-[#FFCC00]/5 hover:-translate-y-1">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-white/40 text-sm mb-1">Total de Cenas</p>
                <p className="text-4xl font-light text-white tracking-tight">{stats.totalScenes}</p>
              </div>
              <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-[#FFCC00]/15 to-[#FFCC00]/5 border border-[#FFCC00]/20 flex items-center justify-center group-hover:shadow-lg group-hover:shadow-[#FFCC00]/20 transition-all">
                <Video className="w-5 h-5 text-[#FFCC00]" />
              </div>
            </div>
          </div>

          {/* Collections */}
          <div className="group relative backdrop-blur-xl bg-white/[0.02] rounded-2xl border border-white/[0.05] p-6 hover:bg-white/[0.04] transition-all duration-500 hover:shadow-2xl hover:shadow-[#FFCC00]/5 hover:-translate-y-1">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-white/40 text-sm mb-1">Coleções</p>
                <p className="text-4xl font-light text-white tracking-tight">{stats.totalCollections}</p>
              </div>
              <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-[#FFCC00]/15 to-[#FFCC00]/5 border border-[#FFCC00]/20 flex items-center justify-center group-hover:shadow-lg group-hover:shadow-[#FFCC00]/20 transition-all">
                <Layers className="w-5 h-5 text-[#FFCC00]" />
              </div>
            </div>
          </div>

          {/* Viral Sounds (Conta D) */}
          <Link 
            to="/admin/sounds"
            className="group relative backdrop-blur-xl bg-white/[0.02] rounded-2xl border border-white/[0.05] p-6 hover:bg-[#FFCC00]/[0.05] hover:border-[#FFCC00]/20 transition-all duration-500 hover:shadow-2xl hover:shadow-[#FFCC00]/10 hover:-translate-y-1"
          >
            <div className="flex items-start justify-between">
              <div>
                <p className="text-white/40 text-sm mb-1">Músicas Virais</p>
                <p className="text-4xl font-light text-white tracking-tight">{stats.totalSounds}</p>
                <p className="text-white/30 text-xs mt-1">Conta D</p>
              </div>
              <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-[#FFCC00]/15 to-[#FFCC00]/5 border border-[#FFCC00]/20 flex items-center justify-center group-hover:shadow-lg group-hover:shadow-[#FFCC00]/20 transition-all">
                <Music className="w-5 h-5 text-[#FFCC00]" />
              </div>
            </div>
          </Link>

          {/* SFX Upload */}
          <Link 
            to="/admin/sfx/add"
            className="group relative backdrop-blur-xl bg-white/[0.02] rounded-2xl border border-white/[0.05] p-6 hover:bg-purple-500/[0.05] hover:border-purple-500/20 transition-all duration-500 hover:shadow-2xl hover:shadow-purple-500/10 hover:-translate-y-1"
          >
            <div className="flex items-start justify-between">
              <div>
                <p className="text-white/40 text-sm mb-1">Efeitos Sonoros</p>
                <p className="text-4xl font-light text-white tracking-tight">SFX</p>
                <p className="text-white/30 text-xs mt-1">Conta E</p>
              </div>
              <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-purple-500/15 to-pink-500/15 border border-purple-500/20 flex items-center justify-center group-hover:shadow-lg group-hover:shadow-purple-500/20 transition-all">
                <Music className="w-5 h-5 text-purple-400" />
              </div>
            </div>
          </Link>

          {/* Overlays Upload */}
          <Link 
            to="/admin/overlays/add"
            className="group relative backdrop-blur-xl bg-white/[0.02] rounded-2xl border border-white/[0.05] p-6 hover:bg-indigo-500/[0.05] hover:border-indigo-500/20 transition-all duration-500 hover:shadow-2xl hover:shadow-indigo-500/10 hover:-translate-y-1"
          >
            <div className="flex items-start justify-between">
              <div>
                <p className="text-white/40 text-sm mb-1">Transições e Efeitos</p>
                <p className="text-4xl font-light text-white tracking-tight">Overlays</p>
                <p className="text-white/30 text-xs mt-1">R2 Storage</p>
              </div>
              <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-indigo-500/15 to-purple-500/15 border border-indigo-500/20 flex items-center justify-center group-hover:shadow-lg group-hover:shadow-indigo-500/20 transition-all">
                <svg className="w-5 h-5 text-indigo-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 4v16M17 4v16M3 8h4m10 0h4M3 12h18M3 16h4m10 0h4M4 20h16a1 1 0 001-1V5a1 1 0 00-1-1H4a1 1 0 00-1 1v14a1 1 0 001 1z" /></svg>
              </div>
            </div>
          </Link>

          {/* Manage Users */}
          <Link 
            to="/admin/users"
            className="group relative backdrop-blur-xl bg-white/[0.02] rounded-2xl border border-white/[0.05] p-6 hover:bg-[#FFCC00]/[0.05] hover:border-[#FFCC00]/20 transition-all duration-500 hover:shadow-2xl hover:shadow-[#FFCC00]/10 hover:-translate-y-1"
          >
            <div className="flex items-start justify-between">
              <div>
                <p className="text-white/40 text-sm mb-1">Gerenciar</p>
                <p className="text-xl font-light text-white tracking-tight">Acessos</p>
              </div>
              <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-[#FFCC00]/15 to-[#FFCC00]/5 border border-[#FFCC00]/20 flex items-center justify-center group-hover:shadow-lg group-hover:shadow-[#FFCC00]/20 transition-all">
                <Users className="w-5 h-5 text-[#FFCC00]" />
              </div>
            </div>
          </Link>

          {/* Add Scenes */}
          <Link 
            to="/admin/scenes/new"
            className="group relative backdrop-blur-xl bg-[#FFCC00]/[0.08] rounded-2xl border border-[#FFCC00]/20 p-6 hover:bg-[#FFCC00]/[0.12] transition-all duration-500 hover:shadow-2xl hover:shadow-[#FFCC00]/20 hover:-translate-y-1"
          >
            <div className="flex items-start justify-between">
              <div>
                <p className="text-[#FFCC00]/60 text-sm mb-1">Adicionar</p>
                <p className="text-xl font-light text-[#FFCC00] tracking-tight">Nova Cena</p>
              </div>
              <div className="w-12 h-12 rounded-2xl bg-[#FFCC00]/20 border border-[#FFCC00]/30 flex items-center justify-center group-hover:shadow-lg group-hover:shadow-[#FFCC00]/30 transition-all">
                <Plus className="w-5 h-5 text-[#FFCC00]" />
              </div>
            </div>
          </Link>
        </div>

        {/* Collections List */}
        <div className="backdrop-blur-xl bg-white/[0.02] rounded-2xl border border-white/[0.05] overflow-hidden">
          <div className="px-6 py-5 border-b border-white/[0.05] flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Sparkles className="w-5 h-5 text-[#FFCC00]/60" />
              <h2 className="text-white font-medium">Coleções</h2>
            </div>
            <Link 
              to="/admin/scenes/new"
              className="flex items-center gap-2 px-4 py-2 rounded-xl bg-[#FFCC00]/10 hover:bg-[#FFCC00]/15 border border-[#FFCC00]/20 text-[#FFCC00] text-sm transition-all hover:shadow-lg hover:shadow-[#FFCC00]/10"
            >
              <Plus className="w-4 h-4" />
              <span>Nova</span>
            </Link>
          </div>
          
          {collections.length === 0 ? (
            <div className="p-16 text-center">
              <div className="w-16 h-16 rounded-2xl bg-white/[0.03] border border-white/[0.05] flex items-center justify-center mx-auto mb-4">
                <FolderOpen className="w-7 h-7 text-white/20" />
              </div>
              <p className="text-white/40">Nenhuma coleção encontrada</p>
              <p className="text-white/20 text-sm mt-1">Adicione cenas para criar coleções</p>
            </div>
          ) : (
            <div className="divide-y divide-white/[0.03]">
              {collections.map((collection, index) => (
                <Link
                  key={collection.id}
                  to={`/admin/collection/${encodeURIComponent(collection.name)}`}
                  className="group flex items-center justify-between px-6 py-4 hover:bg-[#FFCC00]/[0.03] transition-all duration-300"
                  style={{ animationDelay: `${index * 50}ms` }}
                >
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 rounded-xl bg-white/[0.03] border border-white/[0.05] flex items-center justify-center group-hover:bg-[#FFCC00]/10 group-hover:border-[#FFCC00]/20 transition-all">
                      <Film className="w-5 h-5 text-white/40 group-hover:text-[#FFCC00] transition-colors" />
                    </div>
                    <div>
                      <p className="text-white font-medium group-hover:text-[#FFCC00] transition-colors">
                        {collection.name}
                      </p>
                      <p className="text-white/30 text-sm">{collection.pack_count} cenas</p>
                    </div>
                  </div>
                  <ChevronRight className="w-5 h-5 text-white/20 group-hover:text-[#FFCC00] group-hover:translate-x-1 transition-all" />
                </Link>
              ))}
            </div>
          )}
        </div>
        </div>
        )}
      </div>
    </div>
  );
};

export default AdminDashboard;
